import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class FieldMaskingService {
  ngxMaskPhoneNumber = '(000) 000-0000';

  maskedPhoneNumber(phoneNumber: any): any {
    let number = phoneNumber ? phoneNumber.toString().trim().match(/(\d{3})(\d{3})(\d{4})/) : '';
    if (number.length > 2) {
      number = `(${number[1]}) ${number[2]}-${number[3]}`;
    }

    return number;
  }

  /**
   * Custom method to conform a string to a given mask format
   * @param value - The input value that needs to be masked
   * @param mask - The mask pattern (e.g., '000-000' for a number with a dash)
   */
  conformToMask(value: string | string[], mask: string): string {
    // For phone numbers, intelligently choose the mask based on digit count
    // Only apply smart formatting if we have enough digits for a complete phone number
    if (mask === '(000) 000-0000') {
      const digits = (Array.isArray(value) ? value.join('') : value).replace(/\D/g, '');
      // Only use smart formatting if we have 10+ digits
      if (digits.length >= 10) {
        return this.formatPhoneNumber(digits);
      }
    }

    let conformedValue = '';
    let maskIndex = 0;
    let valueIndex = 0;

    while (maskIndex < mask.length && valueIndex < value.length) {
      const maskChar = mask[maskIndex];
      const valueChar = value[valueIndex];

      if (maskChar === '0') {
        if (/\d/.test(valueChar)) {
          conformedValue += valueChar;
          valueIndex += 1;
        } else {
          return conformedValue; // Invalid character
        }
      } else {
        conformedValue += maskChar;
        if (maskChar === valueChar) {
          valueIndex += 1;
        }
      }

      maskIndex += 1;
    }

    return conformedValue;
  }

  /**
   * Intelligently formats phone numbers based on their length
   * @param phoneNumber - The phone number to format
   * @returns Formatted phone number string
   */
  formatPhoneNumber(phoneNumber: string | number): string {
    if (!phoneNumber) return '';

    // Convert to string and remove all non-digit characters
    const digits = phoneNumber.toString().replace(/\D/g, '');

    if (digits.length === 0) return '';

    // Handle different phone number lengths
    switch (digits.length) {
      case 10:
        // Standard US format: (XXX) XXX-XXXX
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;

      case 11:
        // US with country code: +1 (XXX) XXX-XXXX
        if (digits.startsWith('1')) {
          return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
        }
        // Other 11-digit numbers: +XX XXX XXX XXXX
        return `+${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 8)} ${digits.slice(8)}`;

      case 12:
        // International format: +XXX XXX XXX XXXX
        return `+${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 9)} ${digits.slice(9)}`;

      case 13:
        // International format: +XXXX XXX XXX XXXX
        return `+${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 10)} ${digits.slice(10)}`;

      case 14:
        // International format: +XXXXX XXX XXX XXXX
        return `+${digits.slice(0, 5)} ${digits.slice(5, 8)} ${digits.slice(8, 11)} ${digits.slice(11)}`;

      case 15:
        // International format: +XXXXXX XXX XXX XXXX
        return `+${digits.slice(0, 6)} ${digits.slice(6, 9)} ${digits.slice(9, 12)} ${digits.slice(12)}`;

      default:
        // For other lengths, group digits in chunks of 3-4
        if (digits.length <= 3) {
          return digits;
        }
        if (digits.length <= 6) {
          return `${digits.slice(0, 3)} ${digits.slice(3)}`;
        }
        if (digits.length <= 9) {
          return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
        }
        {
          // For very long numbers, group in chunks of 3
          const chunks = [];
          for (let i = 0; i < digits.length; i += 3) {
            chunks.push(digits.slice(i, i + 3));
          }
          return `+${chunks.join(' ')}`;
        }
    }
  }
}
